/**
 * Compare API Handler
 * Manages product comparison functionality with Laravel API
 */

const API_BASE = 'https://app.bepivotal.co.uk/api/v1';
const API_KEY = '7a2918b913ae5bf9e75103ea994aba1df813abd1b185354f3b69304acfd4a17d';
const MAX_COMPARE_ITEMS = 5;

// Get session token (same logic as wishlist)
function getSessionToken() {
  let token = localStorage.getItem('compare_session_token');
  if (!token) {
    token = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('compare_session_token', token);
  }
  return token;
}

// Check if user is logged in
function isUserLoggedIn() {
  return window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.currentVisitorConsent;
}

// Get customer ID if logged in
function getCustomerId() {
  // This should be set by Shopify Liquid in the page
  return window.shopifyCustomerId || null;
}

// Fetch compare list
async function fetchCompareList() {
  try {
    const isLoggedIn = isUserLoggedIn();
    const customerId = getCustomerId();
    const sessionToken = getSessionToken();

    const params = new URLSearchParams();
    if (isLoggedIn && customerId) {
      params.append('is_logged_in', 'true');
      params.append('customer_id', customerId);
    } else {
      params.append('session_token', sessionToken);
    }

    const response = await fetch(`${API_BASE}/compare?${params.toString()}`, {
      method: 'GET',
      headers: {
        'X-Storefront-Key': API_KEY,
        'X-Session-Token': sessionToken,
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return { items: [], count: 0 };
  }
}

// Add product to compare list
async function addToCompare(productId, variantId = null, productTitle = '') {
  try {
    const isLoggedIn = isUserLoggedIn();
    const customerId = getCustomerId();
    const sessionToken = getSessionToken();

    const params = new URLSearchParams();
    if (isLoggedIn && customerId) {
      params.append('is_logged_in', 'true');
      params.append('customer_id', customerId);
    } else {
      params.append('session_token', sessionToken);
    }

    const body = {
      product_id: productId,
    };
    if (variantId) {
      body.variant_id = variantId;
    }

    const response = await fetch(`${API_BASE}/compare/add?${params.toString()}`, {
      method: 'POST',
      headers: {
        'X-Storefront-Key': API_KEY,
        'X-Session-Token': sessionToken,
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 400 && data.message.includes('full')) {
        const titleText = productTitle || 'Compare list';
        showNotification(`${titleText} cannot be added. You can compare up to ${MAX_COMPARE_ITEMS} products.`, 'warning');
      } else if (response.status === 200 && data.message.includes('already')) {
        showNotification('Product already in compare list', 'info');
      } else {
        throw new Error(data.message || 'Failed to add to compare');
      }
      return data;
    }

    showNotification('Added to compare list', 'success', productTitle);
    
    // Update compare count badge
    updateCompareCount(data.count);
    
    // Dispatch event for other scripts
    window.dispatchEvent(new CustomEvent('compareUpdated', { detail: data }));
    
    return data;
  } catch (error) {
    showNotification('Failed to add to compare', 'error');
    throw error;
  }
}

// Remove product from compare list
async function removeFromCompare(productId) {
  try {
    const isLoggedIn = isUserLoggedIn();
    const customerId = getCustomerId();
    const sessionToken = getSessionToken();

    const params = new URLSearchParams();
    if (isLoggedIn && customerId) {
      params.append('is_logged_in', 'true');
      params.append('customer_id', customerId);
    } else {
      params.append('session_token', sessionToken);
    }

    const response = await fetch(`${API_BASE}/compare/remove?${params.toString()}`, {
      method: 'POST',
      headers: {
        'X-Storefront-Key': API_KEY,
        'X-Session-Token': sessionToken,
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
      body: JSON.stringify({ product_id: productId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    showNotification('Removed from compare list', 'success');
    
    // Update compare count badge
    updateCompareCount(data.count);
    
    // Dispatch event for other scripts
    window.dispatchEvent(new CustomEvent('compareUpdated', { detail: data }));
    
    return data;
  } catch (error) {
    showNotification('Failed to remove from compare', 'error');
    throw error;
  }
}

// Clear all items from compare list
async function clearCompareList() {
  try {
    const isLoggedIn = isUserLoggedIn();
    const customerId = getCustomerId();
    const sessionToken = getSessionToken();

    const params = new URLSearchParams();
    if (isLoggedIn && customerId) {
      params.append('is_logged_in', 'true');
      params.append('customer_id', customerId);
    } else {
      params.append('session_token', sessionToken);
    }

    const response = await fetch(`${API_BASE}/compare/clear?${params.toString()}`, {
      method: 'DELETE',
      headers: {
        'X-Storefront-Key': API_KEY,
        'X-Session-Token': sessionToken,
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    showNotification('Compare list cleared', 'success');
    
    // Update compare count badge
    updateCompareCount(0);
    
    // Dispatch event for other scripts
    window.dispatchEvent(new CustomEvent('compareUpdated', { detail: data }));
    
    return data;
  } catch (error) {
    showNotification('Failed to clear compare list', 'error');
    throw error;
  }
}

// Update compare count badge in header/UI
function updateCompareCount(count) {
  const badges = document.querySelectorAll('.compare-count-badge');
  badges.forEach(badge => {
    badge.textContent = count;
    badge.style.display = count > 0 ? 'flex' : 'none';
  });
}

// Show notification with product info and link to compare page
function showNotification(message, type = 'info', productTitle = '') {
  // Remove existing notifications
  const existing = document.querySelector('.compare-notification');
  if (existing) existing.remove();

  // Inject styles if not already present
  if (!document.getElementById('compare-notification-styles')) {
    const style = document.createElement('style');
    style.id = 'compare-notification-styles';
    style.textContent = `
      .compare-notification {
        position: fixed;
        left: 24px;
        bottom: 24px;
        z-index: 9999;
        background: #3D4E5F;
        color: white;
        padding: 18px 24px;
        border-radius: 12px;
        box-shadow: 0 8px 30px rgba(61, 78, 95, 0.35);
        transform: translateX(-120%);
        opacity: 0;
        transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        max-width: 380px;
        font-family: 'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        border: 2px solid rgba(255, 255, 255, 0.1);
      }

      .compare-notification.show {
        transform: translateX(0);
        opacity: 1;
      }

      .compare-notification--success {
        border-left: 5px solid #F85F5F;
      }

      .compare-notification--warning {
        border-left: 5px solid #D5A535;
        background: #3D4E5F;
      }

      .compare-notification--error {
        border-left: 5px solid #F85F5F;
        background: #3D4E5F;
      }

      .compare-notification--info {
        border-left: 5px solid #F6D9D2;
      }

      .compare-notification__content {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .compare-notification__message {
        font-size: 15px;
        font-weight: 600;
        line-height: 1.5;
        letter-spacing: -0.3px;
        color: white;
      }

      .compare-notification__link {
        color: #F6D9D2;
        text-decoration: none;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: -0.2px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s ease;
        width: fit-content;
        padding: 6px 12px;
        background: rgba(246, 217, 210, 0.15);
        border-radius: 6px;
      }

      .compare-notification__link:hover {
        color: white;
        background: #F85F5F;
        transform: translateX(3px);
      }

      .compare-notification__link::after {
        content: '→';
        font-size: 16px;
        transition: transform 0.2s ease;
      }

      .compare-notification__link:hover::after {
        transform: translateX(3px);
      }

      @media (max-width: 768px) {
        .compare-notification {
          left: 16px;
          right: 16px;
          max-width: none;
          padding: 16px 20px;
        }

        .compare-notification__message {
          font-size: 14px;
        }

        .compare-notification__link {
          font-size: 13px;
        }
      }

      @media (max-width: 480px) {
        .compare-notification {
          left: 12px;
          right: 12px;
          bottom: 20px;
          padding: 14px 16px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  const notification = document.createElement('div');
  notification.className = `compare-notification compare-notification--${type}`;
  
  const content = document.createElement('div');
  content.className = 'compare-notification__content';
  
  const messageEl = document.createElement('div');
  messageEl.className = 'compare-notification__message';
  messageEl.textContent = productTitle ? `${productTitle} added to compare.` : message;
  
  const link = document.createElement('a');
  link.className = 'compare-notification__link';
  link.textContent = 'Go to compare page';
  link.href = '/pages/compare';
  
  content.appendChild(messageEl);
  content.appendChild(link);
  notification.appendChild(content);
  
  document.body.appendChild(notification);
  
  // Trigger animation
  setTimeout(() => notification.classList.add('show'), 10);
  
  // Remove after 4 seconds
  setTimeout(() => {
    notification.classList.remove('show');
    setTimeout(() => notification.remove(), 300);
  }, 4000);
}

// Initialize compare functionality
async function initCompare() {
  // Fetch initial compare list to update count
  const data = await fetchCompareList();
  updateCompareCount(data.count);

  // Dispatch event so other components can update
  window.dispatchEvent(new CustomEvent('compareUpdated', { detail: data }));
}

// Global click handler for compare buttons
async function handleCompareClick(productId, variantId) {
  const button = document.querySelector(`[data-compare-button][data-product-id="${productId}"]`);
  if (!button || button.disabled) return;

  button.disabled = true;
  
  // Get product title from the product card
  const productCard = button.closest('.product-card');
  const productTitle = productCard ? productCard.querySelector('.product-name')?.textContent || '' : '';
  
  try {
    const result = await addToCompare(productId, variantId, productTitle);
    
    // Update button state
    if (result) {
      button.classList.add('added');
      
      // Reset after 2 seconds
      setTimeout(() => {
        button.classList.remove('added');
      }, 2000);
    }
  } catch (error) {
    // Error handled silently
  } finally {
    button.disabled = false;
  }
}

// Export functions to window for global access
window.compareAPI = {
  fetch: fetchCompareList,
  add: addToCompare,
  remove: removeFromCompare,
  clear: clearCompareList,
  updateCount: updateCompareCount,
};


// Export global click handler
window.handleCompareClick = handleCompareClick;

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCompare);
} else {
  initCompare();
}
